// Map internal keys to URL parameter names
export const URL_PARAMS = {
  // Filter types
  verifications: "verifications",
  shipmentTypes: "shipment-types",
  rating: "rating",
  experience: "experience",
  wonShipments: "won-shipments",
  page: "page",
  shipmentCategories: "shipment-categories",

  // Verifications
  backgroundCheck: "background-check",
  insurance: "insurance",
  identity: "identity",
  usdot: "usdot",
  usda: "usda",
  manual: "manual",

  // Shipment Types
  "Solo Rides (VIP)": "solo-rides-vip",
  "Door to door delivery": "door-to-door-delivery",
  "GPS Tracking": "gps-tracking",
  "Open trailer hauling": "open-trailer-hauling",
  "Palletized transport": "palletized-transport",
  "Large boat hauling": "large-boat-hauling",
  "Small boat hauling": "small-boat-hauling",
  "Oversized boat hauling": "oversized-boat-hauling",
  "Wrapping protection": "wrapping-protection",
  "Enclosed trailer hauling": "enclosed-trailer-hauling",

  // Experience levels
  new: "new",
  intermediate: "intermediate",
  expert: "expert",

  // Won shipments ranges
  lessThan10: "less-than-10",
  between10And50: "between-10-and-50",
  moreThan50: "more-than-50",
} as const

// Create a reverse mapping for URL values to internal keys
export const REVERSE_URL_PARAMS = Object.entries(URL_PARAMS).reduce(
  (acc, [key, value]) => {
    acc[value] = key
    return acc
  },
  {} as Record<string, string>,
)

export type URLParamKeys = keyof typeof URL_PARAMS
export type URLParamValues = typeof URL_PARAMS[URLParamKeys]

// Convert camelCase to kebab-case
export function toKebabCase(str: string): string {
  return str
    .replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
    .replace(/^-/, "")
}

// Convert kebab-case to camelCase
export function toCamelCase(str: string): string {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
}

// Convert from internal key to URL parameter
export function toURLParam(key: string): string {
  const mappedValue = URL_PARAMS[key as URLParamKeys]
  if (mappedValue) {
    return mappedValue
  }
  return toKebabCase(key)
}

// Convert from URL parameter to internal key
export function fromURLParam(param: string): string {
  const internalKey = REVERSE_URL_PARAMS[param]
  if (internalKey) {
    return internalKey
  }
  return toCamelCase(param)
}

// Convert filter value to URL parameter
export function toURLFilterValue(value: string): string {
  // First check if it's a direct match in URL_PARAMS
  const mappedValue = URL_PARAMS[value as URLParamKeys]
  if (mappedValue) {
    return mappedValue
  }

  // If not found, check if it's already in kebab-case format
  const kebabValue = toKebabCase(value)
  const urlValues = Object.values(URL_PARAMS) as string[]
  if (urlValues.includes(kebabValue)) {
    return kebabValue
  }

  // If still not found, convert to kebab-case
  return kebabValue
}

// Convert URL filter value to internal value
export function fromURLFilterValue(param: string): string {
  // First check if it's a direct match in REVERSE_URL_PARAMS
  const internalValue = REVERSE_URL_PARAMS[param]
  if (internalValue) {
    return internalValue
  }

  // If not found, check if converting to camelCase matches any keys
  const camelValue = toCamelCase(param)
  if (URL_PARAMS[camelValue as URLParamKeys]) {
    return camelValue
  }

  // If still not found, just return the original param
  // This is important for shipment types that might be in their original format
  return param
}

// Clean URL parameters by removing empty values and converting to kebab-case
export function cleanURLParams(params: URLSearchParams): URLSearchParams {
  const cleanParams = new URLSearchParams()

  Array.from(params.entries()).forEach(([key, value]) => {
    if (value && value.trim() !== "") {
      const urlKey = toURLParam(key)
      // Split comma-separated values and convert each
      if (value.includes(",")) {
        const values = value
          .split(",")
          .map((v) => toURLFilterValue(v.trim()))
          .filter(Boolean)
        if (values.length > 0) {
          cleanParams.set(urlKey, values.join(","))
        }
      } else {
        cleanParams.set(urlKey, toURLFilterValue(value))
      }
    }
  })

  return cleanParams
}

// Convert a record of parameters to URLSearchParams with kebab-case keys
export function recordToURLParams(
  record: Record<string, string | string[] | undefined>,
): URLSearchParams {
  const params = new URLSearchParams()

  Object.entries(record).forEach(([key, value]) => {
    if (value) {
      const urlKey = toURLParam(key)
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const urlValues = value.map((v) => toURLFilterValue(v))
          params.set(urlKey, urlValues.join(","))
        }
      } else if (value.trim() !== "") {
        params.set(urlKey, toURLFilterValue(value))
      }
    }
  })

  return params
}

// Convert URLSearchParams to a record with camelCase keys
export function urlParamsToRecord(
  params: URLSearchParams,
): Record<string, string> {
  const record: Record<string, string> = {}

  params.forEach((value, key) => {
    const internalKey = fromURLParam(key)
    if (value.includes(",")) {
      const values = value
        .split(",")
        .map((v) => fromURLFilterValue(v.trim()))
        .filter(Boolean)
      record[internalKey] = values.join(",")
    } else {
      record[internalKey] = fromURLFilterValue(value)
    }
  })

  return record
}

// Type guard to check if a key is a valid URL parameter key
export function isValidURLParamKey(key: string): key is URLParamKeys {
  return key in URL_PARAMS
}

// Type guard to check if a value is a valid URL parameter value
export function isValidURLParamValue(value: string): value is URLParamValues {
  return value in REVERSE_URL_PARAMS
}
